import * as React from "react"
import { motion } from "motion/react"
import { Check } from "lucide-react"

import { cn } from "@/lib/utils"

/* -----------------------------------------------------------------------------
 * StepIndicator Component
 *
 * Visual progress indicator for multi-step flows (login, onboarding, etc.)
 * Supports completed, active, and pending states with animations.
 * -------------------------------------------------------------------------- */

interface Step {
  label: string
  description?: string
}

interface StepIndicatorProps extends React.HTMLAttributes<HTMLDivElement> {
  steps: Step[]
  currentStep: number // 0-indexed
  orientation?: "horizontal" | "vertical"
  size?: "sm" | "default" | "lg"
}

const sizeClasses = {
  sm: {
    circle: "size-6 text-xs",
    connector: "h-0.5",
    verticalConnector: "w-0.5 h-6",
    label: "text-xs",
    description: "text-[10px]",
    gap: "gap-1",
  },
  default: {
    circle: "size-8 text-sm",
    connector: "h-0.5",
    verticalConnector: "w-0.5 h-8",
    label: "text-sm",
    description: "text-xs",
    gap: "gap-1.5",
  },
  lg: {
    circle: "size-10 text-base",
    connector: "h-1",
    verticalConnector: "w-1 h-10",
    label: "text-base",
    description: "text-sm",
    gap: "gap-2",
  },
}

export function StepIndicator({
  steps,
  currentStep,
  orientation = "horizontal",
  size = "default",
  className,
  ...props
}: StepIndicatorProps) {
  const sizes = sizeClasses[size]

  return (
    <div
      className={cn(
        "flex",
        orientation === "horizontal"
          ? "flex-row items-start"
          : "flex-col items-start",
        className
      )}
      role="list"
      aria-label="Progress steps"
      {...props}
    >
      {steps.map((step, index) => {
        const isCompleted = index < currentStep
        const isActive = index === currentStep
        const isPending = index > currentStep

        return (
          <React.Fragment key={index}>
            <div
              className={cn(
                "flex",
                orientation === "horizontal"
                  ? "flex-col items-center"
                  : "flex-row items-start",
                sizes.gap
              )}
              role="listitem"
              aria-current={isActive ? "step" : undefined}
            >
              {/* Step circle */}
              <motion.div
                className={cn(
                  "relative flex items-center justify-center rounded-full font-semibold transition-colors",
                  sizes.circle,
                  isCompleted &&
                    "bg-success text-success-foreground",
                  isActive &&
                    "bg-primary text-primary-foreground ring-4 ring-primary/20",
                  isPending &&
                    "bg-muted text-muted-foreground border border-border"
                )}
                initial={false}
                animate={{
                  scale: isActive ? 1 : 1,
                }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              >
                {isCompleted ? (
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  >
                    <Check className="size-4" strokeWidth={3} />
                  </motion.div>
                ) : (
                  <span>{index + 1}</span>
                )}

                {/* Active pulse ring */}
                {isActive && (
                  <motion.div
                    className="absolute inset-0 rounded-full bg-primary"
                    initial={{ opacity: 0.3, scale: 1 }}
                    animate={{ opacity: 0, scale: 1.5 }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeOut",
                    }}
                  />
                )}
              </motion.div>

              {/* Step label and description */}
              <div
                className={cn(
                  "flex flex-col",
                  orientation === "horizontal" ? "items-center text-center" : "items-start"
                )}
              >
                <span
                  className={cn(
                    "font-medium transition-colors",
                    sizes.label,
                    isCompleted && "text-success",
                    isActive && "text-foreground",
                    isPending && "text-muted-foreground"
                  )}
                >
                  {step.label}
                </span>
                {step.description && (
                  <span
                    className={cn(
                      "text-muted-foreground/70",
                      sizes.description
                    )}
                  >
                    {step.description}
                  </span>
                )}
              </div>
            </div>

            {/* Connector line */}
            {index < steps.length - 1 && (
              <div
                className={cn(
                  "relative flex-1",
                  orientation === "horizontal"
                    ? cn("mx-2 mt-4 min-w-8", sizes.connector)
                    : cn("ml-4 my-1", sizes.verticalConnector)
                )}
              >
                {/* Background line */}
                <div
                  className={cn(
                    "absolute inset-0 rounded-full bg-border",
                    orientation === "horizontal" ? "top-0" : "left-0"
                  )}
                />
                {/* Progress line */}
                <motion.div
                  className={cn(
                    "absolute rounded-full bg-success",
                    orientation === "horizontal"
                      ? "top-0 left-0 h-full"
                      : "top-0 left-0 w-full"
                  )}
                  initial={false}
                  animate={{
                    [orientation === "horizontal" ? "width" : "height"]:
                      isCompleted ? "100%" : "0%",
                  }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                />
              </div>
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

/* -----------------------------------------------------------------------------
 * Compact StepIndicator for space-constrained UIs
 * -------------------------------------------------------------------------- */

interface CompactStepIndicatorProps
  extends React.HTMLAttributes<HTMLDivElement> {
  totalSteps: number
  currentStep: number // 0-indexed
  labels?: string[]
}

export function CompactStepIndicator({
  totalSteps,
  currentStep,
  labels,
  className,
  ...props
}: CompactStepIndicatorProps) {
  return (
    <div
      className={cn("flex items-center gap-2", className)}
      role="progressbar"
      aria-valuenow={currentStep + 1}
      aria-valuemin={1}
      aria-valuemax={totalSteps}
      aria-label={labels?.[currentStep] ?? `Step ${currentStep + 1} of ${totalSteps}`}
      {...props}
    >
      {Array.from({ length: totalSteps }).map((_, index) => {
        const isCompleted = index < currentStep
        const isActive = index === currentStep

        return (
          <motion.div
            key={index}
            className={cn(
              "h-1.5 rounded-full transition-colors",
              isCompleted && "bg-success",
              isActive && "bg-primary",
              !isCompleted && !isActive && "bg-muted"
            )}
            initial={false}
            animate={{
              width: isActive ? 24 : 8,
            }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
          />
        )
      })}
      {labels?.[currentStep] && (
        <span className="ml-2 text-xs font-medium text-muted-foreground">
          {labels[currentStep]}
        </span>
      )}
    </div>
  )
}
